import { type FC, useState } from 'react'

import { Shift } from '@api'

import IconShiftDelete from '@/assets/icons/icon-shift-delete.svg'
import IconShiftEdit from '@/assets/icons/icon-shift-edit.svg'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Tooltip } from '@/components/ui/tooltip'
import type { Creator } from '@/modules/creators/types'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { classNames } from '@/utils/helpers'

import { weekDays } from '../consts'
import type {
  DeleteShiftRequestOptions,
  MutateRepeatableShiftTarget,
} from '../types'
import {
  convertSecondsToHoursOrMinutes,
  formatLocally,
  getShiftDuration,
} from '../utils'
import { ConfirmDeleteDialog } from './dialogs/confirm-delete'
import { DeleteRepeatableShiftDialog } from './dialogs/delete-repeatable-shift'

interface Props {
  shift: Shift
  color: string
  onClickEdit?: (shift: Shift) => void
  onClickDelete?: (options: DeleteShiftRequestOptions) => Promise<void>
  creator: Creator
  areActionsVisible?: boolean
  recurringsUntilEnd?: Shift[]
}

const formatTime = (date: string) => formatLocally(new Date(date), 'hh:mmaaa')
const formatDate = (date: string) =>
  formatLocally(new Date(date), 'EEEE, LLL d, yyyy')

export const ShiftView: FC<Props> = ({
  shift,
  onClickEdit,
  onClickDelete,
  creator,
  areActionsVisible,
  recurringsUntilEnd,
  color,
}: Props) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const [open, setOpen] = useState(false)
  const [isRemovalConfirmationOpen, setIsRemovalConfirmationOpen] =
    useState(false)

  const handleMouseEnter = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleDelete = (target?: MutateRepeatableShiftTarget) => {
    if (!isDeleting && onClickDelete) {
      setIsDeleting(true)
      onClickDelete({ shift, target }).then(() => {
        setIsDeleting(false)
        setIsRemovalConfirmationOpen(false)
        handleClose()
      })
    }
  }

  const handleConfirmNonRepeatableRemoval = () => {
    if (shift.repeatableShiftId) {
      handleDelete('All')
    } else {
      handleDelete()
    }
  }

  const handleClickEdit = () => {
    if (!isDeleting && onClickEdit) {
      onClickEdit(shift)
      handleClose()
    }
  }

  const memberNames = shift.members.map(({ name }) => name).join(',')
  const timeInterval = `${formatTime(shift.startedAt)} - ${formatTime(
    shift.finishedAt,
  )}`
  const duration = convertSecondsToHoursOrMinutes(
    getShiftDuration(shift),
  ).trim()

  const creatorAvatar = (
    <div className="flex items-center space-x-2">
      <CreatorAvatar
        key={creator?.id}
        className="h-8 w-8"
        name={creator.name}
        avatarSrc={creator.avatar.croppedUrl}
      />
      <div className="text-sm">{creator.name}</div>
    </div>
  )

  const repeat =
    shift.days?.length === weekDays.length ? 'Daily' : 'On specific weekdays'

  return (
    <>
      <Popover open={open}>
        <PopoverTrigger
          asChild
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleClose}
        >
          <div className="flex max-w-full cursor-pointer overflow-hidden rounded-md bg-secondary-50 dark:bg-secondary-500">
            <div className={classNames('w-[6px] shrink-0', color)} />
            <div className="max-w-[calc(100%-6px)] px-2 py-[2px]">
              <div className="overflow-hidden text-ellipsis whitespace-nowrap text-sm leading-6">
                {memberNames}
              </div>
              <div className="overflow-hidden text-ellipsis whitespace-nowrap text-xs leading-5 text-secondary-300">
                {timeInterval}
              </div>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent
          sideOffset={12}
          collisionPadding={{ top: 12, bottom: 12 }}
          className="relative w-max max-w-[500px] border-0 p-3 outline-none data-[side=bottom]:animate-slide-down data-[side=top]:animate-slide-up"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleClose}
        >
          <div
            id="content-keeper"
            className="absolute -bottom-3 -top-3 left-0 z-[9] w-full bg-transparent"
          />
          <div className="relative z-10">
            <div className="flex w-full justify-end space-x-3 px-3 pt-3 [&_svg]:cursor-pointer">
              {areActionsVisible && (
                <>
                  <IconShiftDelete
                    onClick={() => setIsRemovalConfirmationOpen(true)}
                  />
                  <Tooltip content="Edit is currently unavailable. Please delete and create a new shift.">
                    <IconShiftEdit
                      className="cursor-not-allowed opacity-50"
                      onClick={(event) => event.preventDefault()}
                    />
                  </Tooltip>
                </>
              )}
            </div>
            <div className="table table-fixed border-separate border-spacing-3">
              <DescriptiveRow
                label="Date"
                description={formatDate(shift.startedAt)}
              />
              <DescriptiveRow
                label="Time"
                description={timeInterval}
              />
              {duration && (
                <DescriptiveRow
                  label="Duration"
                  description={duration}
                />
              )}
              <DescriptiveRow
                label="Recurring"
                description={shift.repeatableShiftId ? repeat : 'Not recurring'}
              />
              <DescriptiveRow
                label="Creator"
                description={creatorAvatar}
              />
              <DescriptiveRow
                label="Team Members"
                description={memberNames}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <DeleteRepeatableShiftDialog
        isOpen={isRemovalConfirmationOpen && !!recurringsUntilEnd?.length}
        onClose={() => setIsRemovalConfirmationOpen(false)}
        shift={shift}
        onDelete={handleDelete}
        isDeleting={isDeleting}
      />
      <ConfirmDeleteDialog
        onDelete={handleConfirmNonRepeatableRemoval}
        isDeleting={isDeleting}
        show={isRemovalConfirmationOpen && !recurringsUntilEnd?.length}
        onClose={() => setIsRemovalConfirmationOpen(false)}
      />
    </>
  )
}

const DescriptiveRow = ({
  label,
  description,
}: {
  label: string
  description: string | JSX.Element
}) => {
  const renderDescription = () => {
    if (typeof description === 'string') {
      return <div>{description}</div>
    }

    return description
  }

  return (
    <div className="table-row text-left text-sm">
      <div className="table-cell text-secondary-300">{label}:</div>
      <div className="table-cell">{renderDescription()}</div>
    </div>
  )
}
